/**
 * **Die Landung beim Einhängen** (#204, Grill 2): Das Bild der gezogenen Zeile gleitet wie
 * ein Magnet in ihren neuen Platz in der Checkliste des Ziels.
 *
 * Das Bild ist eine Kopie des Zieh-Bildes, denn dnd-kit nimmt sein eigenes beim Loslassen
 * weg. Die neuen Zeilen gibt es erst, wenn die Datenbank geschrieben und das Ziel
 * aufgeklappt ist. Ein MutationObserver versteckt sie deshalb, bevor sie gemalt werden.
 * Weil die Karte beim Aufklappen noch wächst, zielt jedes Bild neu auf das aktuelle
 * Rechteck, statt eine feste Bahn abzuspielen.
 */
export function landGhost(source: Element | null, ids: readonly string[], selector: (id: string) => string): () => void {
  const hidden = new Set<HTMLElement>()
  const find = () => ids.map(id => document.querySelector<HTMLElement>(selector(id))).filter((el): el is HTMLElement => el != null)
  const hideNew = () => {
    for (const el of find()) {
      if (hidden.has(el)) continue
      hidden.add(el)
      el.style.opacity = '0'
    }
  }

  const rect = source?.getBoundingClientRect()
  const ghost = source?.cloneNode(true) as HTMLElement | undefined
  if (ghost && rect) {
    ghost.classList.add('nest-landing-ghost')
    // Ohne `animation`: Die Kopie brächte das Abheben des Zieh-Bildes (`drag-lift`, mit
    // `forwards`) mit, und dessen `transform` überschriebe jede Lage — das Bild stand oben links.
    Object.assign(ghost.style, {
      position: 'fixed', margin: '0', pointerEvents: 'none', zIndex: '9999', animation: 'none', transform: 'none',
      left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`,
    })
    document.body.appendChild(ghost)
  }

  const observer = new MutationObserver(hideNew)
  observer.observe(document.body, { childList: true, subtree: true })
  hideNew()

  let frame = 0
  let done = false
  const pos = rect ? { x: rect.left, y: rect.top, w: rect.width, h: rect.height } : null
  const started = performance.now()

  const finish = () => {
    if (done) return
    done = true
    cancelAnimationFrame(frame)
    observer.disconnect()
    ghost?.remove()
    for (const el of hidden) el.style.opacity = ''
  }

  const step = () => {
    const target = find()[0]
    const elapsed = performance.now() - started
    if (!ghost || !pos) {
      if (target || elapsed > 800) return finish()
      frame = requestAnimationFrame(step)
      return
    }
    if (!target) {
      // Kommt keine Zeile (die Datenbank hat abgelehnt), verblasst das Bild an Ort und Stelle.
      if (elapsed > 800) return finish()
      frame = requestAnimationFrame(step)
      return
    }
    const to = target.getBoundingClientRect()
    const pull = 0.22
    pos.x += (to.left - pos.x) * pull
    pos.y += (to.top - pos.y) * pull
    pos.w += (to.width - pos.w) * pull
    pos.h += (to.height - pos.h) * pull
    ghost.style.left = `${pos.x}px`
    ghost.style.top = `${pos.y}px`
    ghost.style.width = `${pos.w}px`
    ghost.style.height = `${pos.h}px`
    const settled = Math.abs(to.left - pos.x) < 0.5 && Math.abs(to.top - pos.y) < 0.5
    if (settled || elapsed > 1200) return finish()
    frame = requestAnimationFrame(step)
  }
  frame = requestAnimationFrame(step)
  // Ohne Bildwechsel (verdecktes Fenster) liefe die Schleife nie weiter — das Bild bliebe liegen
  // (gemessen im E2E, 27.09.2026). Spätestens dann ist Schluss.
  setTimeout(finish, 1500)
  return finish
}
